import { useLayoutEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * How long the surface being left fades out: its curve, EASE_MK over 150ms.
 * EASE_MK is front-loaded: the surface is under 10% opacity by about 50ms
 * and under 5% by about 65ms, and spends the rest of the curve all but
 * gone (an empty plate, R-4).
 */
export const DIP_OUT_MS = 150;

/**
 * When the dip swaps: the curve's visual end (the surface left at about 5%
 * opacity, 65ms into DIP_OUT_MS). It is dropped there (hidden at once) and
 * the next surface starts in, in the same frame: the two never show
 * together, and the plate shows alone for no more than a frame or so.
 */
export const DIP_SWAP_MS = 65;

/**
 * How far into its curve the next surface's fade-in starts (one frame at
 * 60Hz): the curve's first frame is opacity 0, the plate alone; a frame in,
 * EASE_MK has it at about a third. A transition takes it as a negative
 * delay (`transitionDelay: -DIP_IN_SKIP_MS`), an animation as its start time.
 */
export const DIP_IN_SKIP_MS = 16;

/** How long the next surface fades in once the one being left is dropped (EASE_MK over 250ms). */
export const DIP_IN_MS = 250;

/**
 * A dipping surface's opacity transition, keyed on useDip's `visible`:
 * `{ className, style }` for the element that holds the surface `name`.
 * Showing, it fades in over `inMs`, started DIP_IN_SKIP_MS into its curve;
 * leaving, it fades out over DIP_OUT_MS (the curve the swap is timed on);
 * dropped, it is hidden outright, which cuts the fade-out's tail at the swap.
 */
export function dipSurface(visible, name, inMs = DIP_IN_MS) {
    const showing = visible(name);

    return {
        className: cn('transition-opacity ease-mk', showing ? 'opacity-100' : 'opacity-0', visible.gone(name) && 'invisible'),
        style: {
            transitionDuration: `${showing ? inMs : DIP_OUT_MS}ms`,
            transitionDelay: showing ? `-${DIP_IN_SKIP_MS}ms` : undefined,
        },
    };
}

/**
 * Navigation inside a vignette's window, as a dip: when `value` (which
 * surface the story is on) changes, the surface being left fades out first;
 * at the fade's visual end (`swapMs`, DIP_SWAP_MS by default) it is dropped
 * and the next one starts in. The two never show at once (double exposure
 * reads as a glitch).
 *
 * Returns `visible(surface)`: whether that surface is the one showing now,
 * with one more question on it: `visible.gone(surface)`, whether it is
 * neither showing nor fading out (dropped, or never shown). A surface is
 * drawn with an opacity transition (and its entrance, if any) keyed on
 * `visible`: out over DIP_OUT_MS (the curve its swap is timed on), in over
 * whatever its own transition says, started DIP_IN_SKIP_MS into it; and
 * hidden outright (`invisible`) while `gone`, which cuts a fade-out's tail
 * at the swap. With `reduced`, the change is immediate.
 */
export default function useDip(value, { swapMs = DIP_SWAP_MS, reduced = false } = {}) {
    const [shown, setShown] = useState(value);
    const [leaving, setLeaving] = useState(false);

    useLayoutEffect(() => {
        // Back where it was before the leaving surface was dropped (A to B
        // to A inside `swapMs`): nothing is leaving any more, so the surface
        // that was fading comes straight back.
        if (value === shown) {
            setLeaving(false);
            return undefined;
        }
        if (reduced) {
            setShown(value);
            setLeaving(false);
            return undefined;
        }
        setLeaving(true);
        const done = setTimeout(() => {
            setShown(value);
            setLeaving(false);
        }, swapMs);

        return () => clearTimeout(done);
    }, [value, shown, reduced, swapMs]);

    return useMemo(() => {
        const visible = (surface) => surface === shown && !leaving;
        visible.gone = (surface) => surface !== shown;

        return visible;
    }, [shown, leaving]);
}
